import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { nav, footer, cta, brand, type NavEntry, type NavItem } from '../content';
import { Logo, SmartLink, Swipe, Arrow } from './index';
import { BrandedQr } from './brandcode';

// Shared default chrome (Nav + Footer), used by every variant unless it overrides.
// Pages can opt into a dark nav over their first section by giving it data-nav-tone="dark".

const SIGN_IN = 'https://admin.brandedmark.com/';
const STATUS = 'https://status.brandedmark.com';

/** Optional live status in the bar (Variant C's story: which unit, which chapter, how far). */
export type NavStory = { active: boolean; serial: string; chapter: string; progress: number };

// Exported so the static blog (blog/components/SiteNav.tsx) renders the same header styles.
export const CSS = `
.bmc-nav{position:sticky;top:0;z-index:60;transition:background-color .2s var(--bm-ease),box-shadow .2s var(--bm-ease),color .2s var(--bm-ease)}
.bmc-nav[data-tone=light]{background:rgba(255,255,255,.98);box-shadow:0 1px 0 transparent}
.bmc-nav[data-tone=light][data-scrolled=true]{background:rgba(255,255,255,.82);-webkit-backdrop-filter:saturate(1.6) blur(14px);backdrop-filter:saturate(1.6) blur(14px);box-shadow:0 1px 0 var(--bm-line)}
.bmc-nav[data-tone=dark]{background:var(--bm-indigo);color:#fff}
.bmc-nav[data-open=true][data-tone=dark]{background:var(--bm-indigo)}
.bmc-bar{height:72px;display:flex;align-items:center;gap:28px}
.bmc-logo{display:inline-flex;border-radius:10px;flex:none}
.bmc-links{display:none;align-items:center;gap:2px;margin-left:12px;list-style:none;padding:0}
.bmc-links>li{position:relative}
.bmc-trig{display:inline-flex;align-items:center;gap:6px;height:40px;padding:0 13px;border-radius:999px;font-size:15px;font-weight:500;color:var(--bm-indigo);letter-spacing:-.005em;transition:background-color .15s var(--bm-ease),color .15s var(--bm-ease)}
.bmc-trig:hover,.bmc-trig[aria-expanded=true]{background:var(--bm-paper-alt)}
.bmc-nav[data-tone=dark] .bmc-trig{color:rgba(255,255,255,.86)}
.bmc-nav[data-tone=dark] .bmc-trig:hover,.bmc-nav[data-tone=dark] .bmc-trig[aria-expanded=true]{background:rgba(255,255,255,.08);color:#fff}
.bmc-trig[aria-current=page]{color:var(--bm-indigo);font-weight:600}
.bmc-chev{transition:transform .2s var(--bm-ease);opacity:.6}
.bmc-trig[aria-expanded=true] .bmc-chev,.bmc-acc>button[aria-expanded=true] .bmc-chev{transform:rotate(180deg)}
.bmc-right{margin-left:auto;display:flex;align-items:center;gap:6px}
.bmc-signin{display:none;font-size:14px;font-weight:500;color:var(--bm-muted);padding:8px 12px;border-radius:999px;transition:color .15s var(--bm-ease)}
.bmc-signin:hover{color:var(--bm-indigo)}
.bmc-nav[data-tone=dark] .bmc-signin{color:rgba(255,255,255,.7)}
.bmc-nav[data-tone=dark] .bmc-signin:hover{color:#fff}
.v2 .bmc-nav .bmc-cta{display:none}
.bmc-nav[data-tone=dark] .bmc-cta{background:#fff;color:var(--bm-indigo)}
.bmc-nav[data-tone=dark] .bmc-cta:hover{background:var(--bm-lime)}
.bmc-burger{display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:999px;color:var(--bm-indigo);margin-right:-8px}
.bmc-nav[data-tone=dark] .bmc-burger{color:#fff}
.bmc-burger:hover{background:var(--bm-paper-alt)}
.bmc-nav[data-tone=dark] .bmc-burger:hover{background:rgba(255,255,255,.08)}

/* Mega panel */
.bmc-panel{position:absolute;top:calc(100% + 14px);left:-8px;background:#fff;color:var(--bm-ink);border-radius:18px;box-shadow:0 0 0 1px rgba(36,30,97,.07),0 2px 4px rgba(24,19,67,.04),0 12px 24px -6px rgba(24,19,67,.10),0 32px 64px -24px rgba(24,19,67,.22);padding:10px;display:grid;gap:4px;animation:bmc-drop .22s var(--bm-ease) both;transform-origin:top left}
.bmc-panel[data-align=right]{left:auto;right:-8px;transform-origin:top right}
.bmc-panel::before{content:'';position:absolute;left:0;right:0;top:-16px;height:16px}
@keyframes bmc-drop{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.bmc-col-label{font-size:13.5px;font-weight:600;letter-spacing:-.005em;color:var(--bm-muted);padding:12px 14px 6px}
.bmc-groups{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:4px 6px;align-content:start;padding:2px 0 0}
.bmc-group{display:grid;align-content:start;gap:2px}
.bmc-group+.bmc-group{box-shadow:-1px 0 0 var(--bm-line)}
.bmc-group-h{font-size:13.5px;font-weight:600;letter-spacing:-.005em;color:var(--bm-emerald);padding:10px 14px 4px}
.bmc-also{display:inline-flex;align-items:center;gap:6px;margin:-4px 0 4px 14px;padding:4px 0;font-size:13px;font-weight:600;color:var(--bm-muted);transition:color .15s var(--bm-ease)}
.bmc-also:hover,.bmc-also:focus-visible{color:var(--bm-emerald)}
.bmc-now{display:flex;align-items:center;gap:10px;min-width:0;font-size:13px;color:var(--bm-muted);white-space:nowrap;margin-left:auto}
.bmc-now+.bmc-right{margin-left:0}
.bmc-now-serial{font-family:'Geist Mono',ui-monospace,monospace;font-size:12.5px;color:var(--bm-indigo)}
.bmc-now-sep{width:1px;height:14px;background:var(--bm-line)}
.bmc-now-ch{font-weight:600;color:var(--bm-emerald)}
.bmc-progress{position:absolute;left:0;right:0;bottom:-1px;height:3px;background:var(--bm-gradient);transform-origin:left center;transition:transform .12s linear;pointer-events:none}
.bmc-nav[data-story=true] .bmc-signin{display:none}
@media (max-width:559px){.bmc-now-serial,.bmc-now-sep{display:none}.bmc-bar{gap:12px}}
@media (min-width:1080px) and (max-width:1279px){.bmc-nav[data-story=true] .bmc-now-serial,.bmc-nav[data-story=true] .bmc-now-sep{display:none}}
.bmc-item{display:block;padding:11px 14px;border-radius:12px;transition:background-color .15s var(--bm-ease)}
.bmc-item:hover,.bmc-item:focus-visible{background:var(--bm-paper-alt)}
.bmc-item b{display:flex;align-items:center;gap:8px;font-size:15px;font-weight:600;color:var(--bm-indigo);letter-spacing:-.01em}
.bmc-item span{display:block;font-size:13.5px;line-height:1.45;color:var(--bm-muted);margin-top:2px}
.bmc-item .bmc-go{opacity:0;transform:translateX(-4px);transition:opacity .15s var(--bm-ease),transform .15s var(--bm-ease);color:var(--bm-emerald)}
.bmc-item:hover .bmc-go,.bmc-item:focus-visible .bmc-go{opacity:1;transform:none}
.bmc-feature{border-radius:12px;background:var(--bm-indigo);color:#fff;padding:20px;display:flex;flex-direction:column;gap:10px;position:relative;overflow:hidden}
.bmc-feature h3{font-size:19px;font-weight:700;letter-spacing:-.02em;line-height:1.2}
.bmc-feature p{font-size:13.5px;line-height:1.5;color:rgba(255,255,255,.72)}
.bmc-feature a{display:inline-flex;align-items:center;gap:8px;font-size:14px;font-weight:600;color:var(--bm-lime)}
.bmc-feature a:hover{text-decoration:underline;text-underline-offset:4px}
.bmc-feature .bmc-scan{margin-top:auto;display:flex;align-items:center;gap:12px;border-top:1px solid rgba(255,255,255,.12);padding-top:14px;font-size:12.5px;color:rgba(255,255,255,.66)}
.bmc-foot{grid-column:1/-1;display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:4px;padding:12px 14px 6px;border-top:1px solid var(--bm-line);font-size:13px;color:var(--bm-muted)}
.bmc-foot a{color:var(--bm-indigo);font-weight:600;display:inline-flex;align-items:center;gap:6px}
.bmc-foot a:hover{color:var(--bm-emerald)}

/* Mobile sheet */
.bmc-sheet{position:fixed;inset:0;z-index:80;background:#fff;display:flex;flex-direction:column;animation:bmc-sheet .28s var(--bm-ease) both}
@keyframes bmc-sheet{from{transform:translateY(-12px);opacity:0}to{transform:none;opacity:1}}
.bmc-sheet-top{height:72px;display:flex;align-items:center;justify-content:space-between;padding:0 24px;border-bottom:1px solid var(--bm-line);flex:none}
.bmc-sheet-body{flex:1;overflow-y:auto;padding:8px 24px 24px;-webkit-overflow-scrolling:touch}
.bmc-acc{border-bottom:1px solid var(--bm-line)}
.bmc-acc>button,.bmc-acc>a{width:100%;display:flex;align-items:center;justify-content:space-between;min-height:64px;font-size:22px;font-weight:700;letter-spacing:-.025em;color:var(--bm-indigo);text-align:left}
.bmc-acc-list{padding:0 0 14px;display:grid}
.bmc-acc-list a{display:flex;flex-direction:column;justify-content:center;padding:8px 0;min-height:46px}
.bmc-acc-list a b{display:block;font-size:16px;font-weight:600;color:var(--bm-indigo)}
.bmc-acc-list a span{display:block;font-size:14px;color:var(--bm-muted);line-height:1.4;margin-top:1px}
.bmc-acc-list a.bmc-acc-lead b{color:var(--bm-emerald)}
.bmc-acc-h{font-size:13.5px;font-weight:600;color:var(--bm-emerald);padding:18px 0 2px;margin-top:4px;border-top:1px solid var(--bm-line)}

.bmc-sheet-meta{display:flex;flex-wrap:wrap;gap:8px 20px;padding-top:20px;font-size:14.5px;color:var(--bm-muted)}
.bmc-sheet-meta a{text-decoration:underline;text-underline-offset:4px;text-decoration-color:var(--bm-line)}
.bmc-sheet-cta{flex:none;padding:14px 24px calc(14px + env(safe-area-inset-bottom));border-top:1px solid var(--bm-line);display:grid;gap:10px;background:#fff}
.bmc-sheet-cta .btn{width:100%;min-height:54px;font-size:16.5px}

@media (min-width:1080px){
  .bmc-links{display:flex}
  .bmc-signin{display:inline-flex}
  .v2 .bmc-nav .bmc-cta{display:inline-flex}
  .bmc-burger{display:none}
}
@media (min-width:640px) and (max-width:1079px){ .v2 .bmc-nav .bmc-cta{display:inline-flex} }

/* Footer */
.bmc-footer{background:var(--bm-indigo-deep);color:#fff;position:relative;overflow:hidden}
.bmc-sign[hidden]{display:none!important}
.bmc-sign{padding:clamp(56px,7vw,96px) 0 clamp(44px,5vw,72px);display:grid;gap:36px;align-items:end;border-bottom:1px solid rgba(255,255,255,.1)}
.bmc-sign h2{font-size:clamp(40px,6vw,88px);font-weight:800;letter-spacing:-.05em;line-height:.95;color:#fff;max-width:9.5em;text-wrap:balance}
.bmc-sign-cta{display:flex;flex-wrap:wrap;align-items:center;gap:8px 16px}
.bmc-quiet{font-size:15.5px;color:rgba(255,255,255,.75);text-decoration:underline;text-decoration-color:rgba(255,255,255,.3);text-underline-offset:6px}
.bmc-quiet:hover{color:#fff;text-decoration-color:var(--bm-lime)}
.bmc-quiet-d{justify-self:center;padding:6px 12px;font-size:15px;color:var(--bm-muted);text-decoration:underline;text-decoration-color:var(--bm-line);text-underline-offset:5px}
.bmc-cols{display:grid;gap:40px 24px;padding:56px 0 48px;grid-template-columns:repeat(2,minmax(0,1fr))}
.bmc-brandcol{grid-column:1/-1;display:grid;gap:18px;align-content:start}
.bmc-col h3{font-size:14px;font-weight:600;letter-spacing:-.005em;color:rgba(255,255,255,.55)}
.bmc-col ul{margin-top:16px;display:grid;gap:4px;list-style:none;padding:0}
.bmc-col a{display:inline-block;padding:5px 0;font-size:15px;color:rgba(255,255,255,.82);transition:color .15s var(--bm-ease)}
.bmc-col a:hover{color:var(--bm-lime)}
.bmc-pill{display:inline-flex;align-items:center;gap:8px;font-size:13px;color:rgba(255,255,255,.78);padding:7px 12px;border-radius:999px;box-shadow:inset 0 0 0 1px rgba(255,255,255,.16);transition:box-shadow .15s var(--bm-ease),color .15s var(--bm-ease)}
.bmc-pill:hover{box-shadow:inset 0 0 0 1px rgba(255,255,255,.5);color:#fff}
.bmc-dot{width:7px;height:7px;border-radius:50%;background:var(--bm-green-mid);box-shadow:0 0 0 3px rgba(143,213,58,.18)}
.bmc-legal{border-top:1px solid rgba(255,255,255,.1);padding:24px 0 32px;display:flex;flex-wrap:wrap;gap:12px 32px;justify-content:space-between;font-size:13px;line-height:1.6;color:rgba(255,255,255,.5)}
.bmc-legal a{color:rgba(255,255,255,.72)}
.bmc-legal a:hover{color:#fff}
@media (min-width:768px){
  .bmc-sign{grid-template-columns:1fr auto}
  .bmc-cols{grid-template-columns:1.5fr repeat(4,minmax(0,1fr))}
  .bmc-brandcol{grid-column:auto}
}
@media (prefers-reduced-motion:reduce){.bmc-panel,.bmc-sheet{animation:none}}
`;

const Chevron = ({ size = 12 }: { size?: number }) => (
  <svg className="bmc-chev" width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 4.5 6 7.5l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function useTone() {
  const { pathname } = useLocation();
  const [tone, setTone] = useState<'light' | 'dark'>('light');
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const check = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      // A dark first section (data-nav-tone="dark" at the top of the page) gets a matching bar
      const first = document.querySelector<HTMLElement>('#main [data-nav-tone="dark"]');
      const r = first?.getBoundingClientRect();
      const atTop = !!r && r.top + y < 140;
      // Stay dark while the dark section is still behind the bar
      setTone(atTop && r!.bottom > 72 ? 'dark' : 'light');
    };
    check();
    // first section may mount a tick later (variant switch, lazy pages)
    const t = window.setTimeout(check, 50);
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [pathname]);
  return { tone, scrolled };
}

function PanelItem({ i, onClose }: { i: NavItem; onClose: () => void }) {
  return (
    <>
      <SmartLink href={i.href} className="bmc-item" onClick={onClose}>
        <b>
          {i.label} <span className="bmc-go"><Arrow /></span>
        </b>
        <span>{i.desc}</span>
      </SmartLink>
      {i.also && (
        <SmartLink href={i.also.href} className="bmc-also" onClick={onClose}>
          {i.also.label} <Arrow />
        </SmartLink>
      )}
    </>
  );
}

/** The dropdown for one top-level entry. Shape follows the data: groups (Platform), many items (two columns), few (one column). */
export function MegaPanel({ entry, id, onClose }: { entry: NavEntry; id: string; onClose: () => void }) {
  const items = entry.items ?? [];
  const label = `${entry.label} menu`;
  if (entry.groups) {
    const f = entry.feature;
    return (
      <div id={id} className="bmc-panel" style={{ width: 980, gridTemplateColumns: f ? '232px 1fr' : '1fr', columnGap: 10 }} role="region" aria-label={label}>
        {f && (
          <div className="bmc-feature" style={{ gridRow: '1 / 3' }}>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
            <SmartLink href={f.cta.href} onClick={onClose}>
              {f.cta.label} <Arrow />
            </SmartLink>
            <div className="bmc-scan">
              <BrandedQr accent="#FFFFFF" mark="K" ink="#241E61" seed="KAW9-26-04182" size={64} />
              <span>
                One fictional Halvard heat pump.
                <br />
                No sign-up. About three minutes.
              </span>
            </div>
          </div>
        )}
        <div className="bmc-groups">
          {entry.groups.map((g) => (
            <div key={g.title} className="bmc-group" role="group" aria-label={g.title}>
              <div className="bmc-group-h">{g.title}</div>
              {g.items.map((i) => (
                <PanelItem key={i.href} i={i} onClose={onClose} />
              ))}
            </div>
          ))}
        </div>
        {f?.more && (
          <div className="bmc-foot" style={{ gridColumn: 'auto' }}>
            <span>New here? {f.more.desc}</span>
            <SmartLink href={f.more.href} onClick={onClose}>
              {f.more.label} <Arrow />
            </SmartLink>
          </div>
        )}
      </div>
    );
  }
  if (items.length > 4) {
    return (
      <div id={id} className="bmc-panel" style={{ width: 720, gridTemplateColumns: '1fr 1fr' }} role="region" aria-label={label}>
        <div className="bmc-col-label" style={{ gridColumn: '1 / -1' }}>Built for manufacturers who sell through dealers and installers</div>
        {items.map((i) => (
          <PanelItem key={i.href} i={i} onClose={onClose} />
        ))}
        <div className="bmc-foot">
          <span>{entry.foot}</span>
          <SmartLink href={entry.href} onClick={onClose}>
            All {entry.label.toLowerCase()} <Arrow />
          </SmartLink>
        </div>
      </div>
    );
  }
  return (
    <div id={id} className="bmc-panel" data-align="right" style={{ width: 360 }} role="region" aria-label={label}>
      {items.map((i) => (
        <PanelItem key={i.href} i={i} onClose={onClose} />
      ))}
    </div>
  );
}

export function SharedNav({ story }: { story?: NavStory } = {}) {
  const { pathname } = useLocation();
  const { tone, scrolled } = useTone();
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  const closeTimer = useRef<number>();
  const openTimer = useRef<number>();
  const navRef = useRef<HTMLElement>(null);
  const trigRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const burgerRef = useRef<HTMLButtonElement>(null);
  const uid = useId().replace(/:/g, '');
  const entries = nav.primary;
  // The booking page has its own booking card; the nav doesn't repeat the button there.
  const onBook = pathname.replace(/\/$/, '') === cta.primaryHref;

  const close = useCallback(() => setOpen(null), []);

  // Close everything on navigation
  useEffect(() => {
    setOpen(null);
    setSheet(false);
  }, [pathname]);

  // Outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const t = trigRefs.current[open];
        setOpen(null);
        t?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Mobile sheet: lock scroll, Escape to close
  useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSheet(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [sheet]);

  const hoverOpen = (label: string) => {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => setOpen(label), open ? 0 : 90);
  };
  const hoverClose = () => {
    window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(null), 180);
  };

  const onTrigKey = (e: React.KeyboardEvent, label: string) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(label);
      requestAnimationFrame(() => document.querySelector<HTMLElement>(`#${uid}-${label} a`)?.focus());
    }
  };

  const onBlurWithin = (e: React.FocusEvent) => {
    if (navRef.current && !navRef.current.contains(e.relatedTarget as Node)) setOpen(null);
  };

  const isActive = (href: string) => href !== '/' && pathname.startsWith(href.replace(/#.*/, ''));

  return (
    <>
      <style>{CSS}</style>
      <header ref={navRef} className="bmc-nav" data-tone={sheet ? 'light' : tone} data-scrolled={scrolled} data-open={!!open} data-story={!!story?.active} onBlur={onBlurWithin}>
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[90] focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-[#241E61]">
          Skip to content
        </a>
        <div className="wrap bmc-bar">
          <SmartLink href="/" className="bmc-logo" aria-label="BrandedMark home">
            <Logo reversed={tone === 'dark' && !sheet} size={34} />
          </SmartLink>
          <nav aria-label="Main">
            <ul className="bmc-links">
              {entries.map((n) => {
                const pid = `${uid}-${n.label}`;
                if (!n.items) {
                  return (
                    <li key={n.label}>
                      <SmartLink href={n.href} className="bmc-trig" aria-current={isActive(n.href) ? 'page' : undefined}>
                        {n.label}
                      </SmartLink>
                    </li>
                  );
                }
                return (
                  <li key={n.label} onMouseEnter={() => hoverOpen(n.label)} onMouseLeave={hoverClose}>
                    <button
                      ref={(el) => (trigRefs.current[n.label] = el)}
                      type="button"
                      className="bmc-trig"
                      aria-expanded={open === n.label}
                      aria-controls={pid}
                      onClick={() => setOpen((o) => (o === n.label ? null : n.label))}
                      onKeyDown={(e) => onTrigKey(e, n.label)}
                    >
                      {n.label}
                      <Chevron />
                    </button>
                    {open === n.label && <MegaPanel entry={n} id={pid} onClose={close} />}
                  </li>
                );
              })}
            </ul>
          </nav>
          {story && (
            <div className="bmc-now" aria-live="polite">
              {story.active && (
                <>
                  <span className="bmc-now-serial">{story.serial}</span>
                  <span className="bmc-now-sep" />
                  <span className="bmc-now-ch">{story.chapter}</span>
                </>
              )}
            </div>
          )}
          <div className="bmc-right">
            <a href={SIGN_IN} className="bmc-signin">
              Sign in
            </a>
            {!onBook && (
              <SmartLink href={cta.primaryHref} className="btn btn-primary btn-sm bmc-cta">
                {cta.primary}
              </SmartLink>
            )}
            <button ref={burgerRef} type="button" className="bmc-burger" aria-label="Open menu" aria-expanded={sheet} onClick={() => setSheet(true)}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <path d="M3 7h16M3 15h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
        {story && <div className="bmc-progress" aria-hidden="true" style={{ transform: `scaleX(${story.active ? story.progress : 0})` }} />}
      </header>
      {sheet && <MobileSheet entries={entries} showBook={!onBook} onClose={() => setSheet(false)} returnFocus={() => burgerRef.current?.focus()} />}
    </>
  );
}

function MobileSheet({ entries, showBook, onClose, returnFocus }: { entries: NavEntry[]; showBook: boolean; onClose: () => void; returnFocus: () => void }) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => closeBtn.current?.focus(), []);
  const done = () => {
    onClose();
    returnFocus();
  };
  return (
    <div className="bmc-sheet" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="bmc-sheet-top">
        <SmartLink href="/" onClick={onClose} aria-label="BrandedMark home">
          <Logo size={34} />
        </SmartLink>
        <button ref={closeBtn} type="button" className="bmc-burger" aria-label="Close menu" onClick={done}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path d="M5 5l12 12M17 5 5 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <nav className="bmc-sheet-body" aria-label="Main">
        {entries.map((n) =>
          n.items ? (
            <div key={n.label} className="bmc-acc">
              <button type="button" aria-expanded={openGroup === n.label} onClick={() => setOpenGroup((g) => (g === n.label ? null : n.label))}>
                {n.label}
                <Chevron size={18} />
              </button>
              {openGroup === n.label && (
                <div className="bmc-acc-list">
                  {n.feature?.more && (
                    <SmartLink href={n.feature.more.href} onClick={onClose} className="bmc-acc-lead">
                      <b>{n.feature.more.label}</b>
                    </SmartLink>
                  )}
                  {n.groups
                    ? n.groups.map((g) => (
                        <div key={g.title} role="group" aria-label={g.title} style={{ display: 'grid' }}>
                          <div className="bmc-acc-h">{g.title}</div>
                          {g.items.flatMap((i) => [i, ...(i.also ? [{ ...i.also, desc: '' }] : [])]).map((i) => (
                            <SmartLink key={i.href} href={i.href} onClick={onClose}>
                              <b>{i.label}</b>
                            </SmartLink>
                          ))}
                        </div>
                      ))
                    : n.items.map((i) => (
                        <SmartLink key={i.href} href={i.href} onClick={onClose}>
                          <b>{i.label}</b>
                        </SmartLink>
                      ))}
                  {!n.groups && n.items.length > 4 && (
                    <SmartLink href={n.href} onClick={onClose} className="bmc-acc-lead">
                      <b>All {n.label.toLowerCase()}</b>
                    </SmartLink>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div key={n.label} className="bmc-acc">
              <SmartLink href={n.href} onClick={onClose}>
                {n.label}
                <span style={{ opacity: 0.5 }}>
                  <Arrow />
                </span>
              </SmartLink>
            </div>
          )
        )}
        <div className="bmc-sheet-meta">
          <a href={SIGN_IN}>Sign in</a>
          <a href={STATUS} target="_blank" rel="noopener">
            System status
          </a>
        </div>
      </nav>
      <div className="bmc-sheet-cta">
        {showBook ? (
          <>
            <SmartLink href={cta.primaryHref} className="btn btn-primary" onClick={onClose}>
              {cta.primary} <Arrow />
            </SmartLink>
            <SmartLink href={cta.secondaryHref} className="bmc-quiet-d" onClick={onClose}>
              or {cta.secondary.toLowerCase()}
            </SmartLink>
          </>
        ) : (
          <SmartLink href={cta.secondaryHref} className="bmc-quiet-d" onClick={onClose}>
            Not ready to book? {cta.secondary}
          </SmartLink>
        )}
      </div>
    </div>
  );
}

// Pages that already close on the sign-off line; the footer doesn't repeat it there.
const OWN_SIGNOFF = new Set(['/why']);

export function SharedFooter() {
  const { pathname } = useLocation();
  return (
    <footer className="bmc-footer on-dark">
      <style>{CSS}</style>
      <div className="wrap">
        <div className="bmc-sign" hidden={OWN_SIGNOFF.has(pathname)}>
          <h2>
            Make owning things <Swipe>better.</Swipe>
          </h2>
        </div>
        <div className="bmc-cols">
          <div className="bmc-brandcol">
            <Logo reversed size={36} />
            <p style={{ fontSize: 15, lineHeight: 1.55, color: 'rgba(255,255,255,.66)', maxWidth: 280 }}>{brand.line}.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <span className="bmc-pill">
                <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m4.5 10.5 3.5 3.5 7.5-8" stroke="var(--bm-green-mid)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg> GDPR
              </span>
              <a className="bmc-pill" href={STATUS} target="_blank" rel="noopener">
                System status
              </a>
            </div>
          </div>
          {footer.columns.map((c) => (
            <div key={c.t} className="bmc-col">
              <h3>{c.t}</h3>
              <ul>
                {c.links.map((l) => (
                  <li key={l.h}>
                    <SmartLink href={l.h}>{l.l}</SmartLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="bmc-legal">
          <p style={{ maxWidth: 720 }}>
            © {new Date().getFullYear()} {footer.legal}
          </p>
          <p style={{ display: 'flex', gap: 20 }}>
            <a href={SIGN_IN}>Sign in</a>
            <SmartLink href="/legal/">Legal hub</SmartLink>
          </p>
        </div>
      </div>
    </footer>
  );
}

